import authenticatedAxiosInstance from './authenticatedAxiosInstance.service';
import authHeader from './auth-header';

const API_URL = process.env.REACT_APP_API_URL + '/imports';

const routes = {
  SUMMARY: `${API_URL}/summary`,
  VIN_TO_STOCK_NUM_XREF: `${API_URL}/vin-to-stock-num-xref`,
  BHPH_LOAN_REQUESTS: `${API_URL}/bhph-loan-requests`,
  UNSOLD_INVENTORY: `${API_URL}/unsold-inventory`,
  WHOLESALE_INVENTORY: `${API_URL}/wholesale-inventory`,
  REF_SWAP_REQUESTS: `${API_URL}/ref-swap-requests`,
  PREMIER_LOAN_REQUESTS: `${API_URL}/premier-loan-requests`,
  INTEREST_BALANCE: `${API_URL}/interest-balance`,
  AR_AGED: `${API_URL}/ar-aged`,
  LOAN_REQUESTS: `${API_URL}/loan-requests`,
  FLOORPLAN_REQUESTS: `${API_URL}/floorplan-requests`,
  ACC_DEPRECIATION: `${API_URL}/acc-depreciation`,
  AR_BALANCE: `${API_URL}/ar-balance`,
  INDIRECT_LOAN_REQUESTS: `${API_URL}/indirect-loan-requests`,
  CHECK_FOR_DUPLICATES: `${API_URL}/check-for-duplicates`,
  CLEAR_DEFERRAL_FLAGS: `${API_URL}/clear-deferral-flags`,
  CURRENT_DEALER_AR: `${API_URL}/current-dealer-ar`,
  IMPORT_ACC_DEPRECIATION: `${API_URL}/acc-depreciation/import-data`,
  IMPORT_AR_AGED: `${API_URL}/ar-aged/import-data`,
  IMPORT_AR_BALANCE: `${API_URL}/ar-balance/import-data`,
  IMPORT_BHPH_LOAN_REQUESTS: `${API_URL}/bhph-loan-requests/import-data`,
  IMPORT_FLOORPLAN_REQUESTS: `${API_URL}/floorplan-requests/import-data`,
  IMPORT_INDIRECT_LOADN_REQUESTS: `${API_URL}/indirect-loan-requests/import-data`,
  IMPORT_INTEREST_BALANCE: `${API_URL}/interest-balance/import-data`,
  IMPORT_LOAN_REQUEST: `${API_URL}/loan-requests/import-data`,
  IMPORT_PREMIER_LOAN_REQUEST: `${API_URL}/premier-loan-requests/import-data`,
  IMPORT_REF_SWAP_REQUESTS: `${API_URL}/ref-swap-requests/import-data`,
  IMPORT_UNSOLD_INVENTORY: `${API_URL}/unsold-inventory/import-data`,
  IMPORT_VIN_TO_STOCK_NUM_XREF: `${API_URL}/vin-to-stock-num-xref/import-data`,
  IMPORT_WHOLESALE_INVENTORY: `${API_URL}/wholesale-inventory/import-data`,
  UPLOAD_MONITOR_AR_AGED: `${API_URL}/upload-monitor/ar-aged`,
  UPLOAD_MONITOR_AR_BALANCE: `${API_URL}/upload-monitor/ar-balance`,
  UPLOAD_MONITOR_ACC_DEPRECIATION: `${API_URL}/upload-monitor/acc-depreciation`,
  UPLOAD_MONITOR_INTEREST_BALANCE: `${API_URL}/upload-monitor/interest-balance`,
  UPLOAD_MONITOR_VIN_TO_STOCK_NUM_XREF: `${API_URL}/upload-monitor/vin-to-stock-num-xref`,
  UPLOAD_MONITOR_FLOORPLAN_REQUEST: `${API_URL}/upload-monitor/floorplan-request`,
  UPLOAD_MONITOR_LOAN_REQUEST: `${API_URL}/upload-monitor/loan-request`,
  UPLOAD_MONITOR_LOAN_REQUEST_DETAIL: `${API_URL}/upload-monitor/loan-request/detail`,
  UPLOAD_MONITOR_PREMIER_LOAN_REQUEST: `${API_URL}/upload-monitor/premier-loan-request`,
  UPLOAD_MONITOR_INDIRECT_LOAN_REQUEST: `${API_URL}/upload-monitor/indirect-loan-request`,
  UPLOAD_MONITOR_BHPH_LOAN_REQUEST: `${API_URL}/upload-monitor/bhph-loan-request`,
  UPLOAD_MONITOR_UNSOLD_INVENTORY: `${API_URL}/upload-monitor/unsold-inventory`,
  UPLOAD_MONITOR_WHolesale_INVENTORY: `${API_URL}/upload-monitor/wholesale-inventory`,
  UPLOAD_MONITOR_AR_AGED_DETAIL: `${API_URL}/upload-monitor/ar-aged-detail`,
  UPLOAD_MONITOR_AR_BALANCE_DETAIL: `${API_URL}/upload-monitor/ar-balance-detail`,
  UPLOAD_MONITOR_ACC_DEPRECIATION_DETAIL: `${API_URL}/upload-monitor/acc-depreciation-detail`,
  UPLOAD_MONITOR_BHPH_LOAN_REQUEST_DETAIL: `${API_URL}/upload-monitor/bhph-loan-request-detail`,
  UPLOAD_MONITOR_FLOORPLAN_REQUEST_DETAIL: `${API_URL}/upload-monitor/floorplan-request-detail`,
  UPLOAD_MONITOR_INDIRECT_LOAN_REQUEST_DETAIL: `${API_URL}/upload-monitor/indirect-loan-request-detail`,
  UPLOAD_MONITOR_INTEREST_BALANCE_DETAIL: `${API_URL}/upload-monitor/interest-balance-detail`,
  UPLOAD_MONITOR_PREMIER_LOAN_REQUEST_DETAIL: `${API_URL}/upload-monitor/premier-loan-request-detail`,
  UPLOAD_MONITOR_REFI_SWAP_DETAIL: `${API_URL}/upload-monitor/refi-swap-detail`,
  UPLOAD_MONITOR_UNSOLD_INVENTORY_DETAIL: `${API_URL}/upload-monitor/unsold-inventory-detail`,
  UPLOAD_MONITOR_VIN_TO_STOCK_DETAIL: `${API_URL}/upload-monitor/vin-to-stock-detail`,
  UPLOAD_MONITOR_WHOLESALE_INVENTORY_DETAIL: `${API_URL}/upload-monitor/wholesale-inventory-detail`,
  AR_AGED_IMPORT_STATUS: `${API_URL}/ar-aged`,
  VALIDATE: `${API_URL}/validate`,
  CHECK_PRIOR: `${API_URL}/check-prior`,
};

class ImportsService {
  async validateImportFile(file: File, importType: string, DMS: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('importType', importType);
    formData.append('DMS', DMS);
    return authenticatedAxiosInstance.post(routes.VALIDATE, formData, {
      headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
    });
  }

  // Layer 1 of duplicate-detection (see docs/IMPORT_DUPLICATE_DETECTION_BUG.md).
  // Used by the upload modal to warn before re-importing the same file/type.
  // `importType` is the slug (e.g. 'unsold-inventory'); the backend maps it
  // to the canonical ImportWhat string.
  async checkPriorImport(compId: number | string, importType: string, filename?: string) {
    return authenticatedAxiosInstance.get(routes.CHECK_PRIOR, {
      headers: authHeader(),
      params: { compId, importType, filename: filename || '' },
    });
  }

  async clearDeferralFlags() {
    return authenticatedAxiosInstance.get(routes.CLEAR_DEFERRAL_FLAGS, { headers: authHeader() });
  }

  async importsSummary(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(routes.SUMMARY, { headers: authHeader(), signal });
  }

  async vinToStockNumXref(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.VIN_TO_STOCK_NUM_XREF}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async bhphLoanRequests(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.BHPH_LOAN_REQUESTS}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async unsoldInventory(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.UNSOLD_INVENTORY}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async deleteUnsoldInventory(logid: string) {
    return authenticatedAxiosInstance.delete(`${routes.UNSOLD_INVENTORY}/${logid}`, {
      headers: authHeader(),
    });
  }

  async wholesaleInventory(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.WHOLESALE_INVENTORY}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async deleteWholesaleInventory(logid: string) {
    return authenticatedAxiosInstance.delete(`${routes.WHOLESALE_INVENTORY}/${logid}`, {
      headers: authHeader(),
    });
  }

  async refSwapRequests(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.REF_SWAP_REQUESTS}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async premierLoanRequests(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.PREMIER_LOAN_REQUESTS}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async interestBalance(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.INTEREST_BALANCE}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async deleteInterestBalance(logid: string) {
    return authenticatedAxiosInstance.delete(`${routes.INTEREST_BALANCE}/${logid}`, {
      headers: authHeader(),
    });
  }

  async arAged(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.AR_AGED}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async deleteArAged(logid: string) {
    return authenticatedAxiosInstance.delete(`${routes.AR_AGED}/${logid}`, {
      headers: authHeader(),
    });
  }

  async loanRequests(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.LOAN_REQUESTS}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async floorplanRequests(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.FLOORPLAN_REQUESTS}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async accDepreciation(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.ACC_DEPRECIATION}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async arBalance(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.AR_BALANCE}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async deleteArBalance(logid: string) {
    return authenticatedAxiosInstance.delete(`${routes.AR_BALANCE}/${logid}`, {
      headers: authHeader(),
    });
  }

  async indirectLoanRequests(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.INDIRECT_LOAN_REQUESTS}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async checkForDuplicates() {
    return authenticatedAxiosInstance.get(routes.CHECK_FOR_DUPLICATES, { headers: authHeader() });
  }

  async currentDealerAR(CompId?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.CURRENT_DEALER_AR}/${CompId}`, {
      headers: authHeader(),
      signal,
    });
  }

  async deleteAccDepreciation(logid: string) {
    return authenticatedAxiosInstance.delete(`${routes.ACC_DEPRECIATION}/${logid}`, {
      headers: authHeader(),
    });
  }

  async importAccDepreciation(
    DMS: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_ACC_DEPRECIATION}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            options.onUploadProgress?.(Math.min(percent, 100));
          }
        : undefined,
    });
  }

  async getAccDepreciationImportStatus(compId: string, logId: number | string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${routes.ACC_DEPRECIATION}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importARAged(
    DMS: string,
    bhphOn: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('bhphOn', bhphOn);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_AR_AGED}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            options.onUploadProgress?.(Math.min(percent, 100));
          }
        : undefined,
    });
  }

  async importARBalance(
    DMS: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_AR_BALANCE}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            options.onUploadProgress?.(Math.min(percent, 100));
          }
        : undefined,
    });
  }

  async getArBalanceImportStatus(compId: string, logId: number | string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${routes.AR_BALANCE}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importBHPHLoanRequests(
    DMS: string,
    Code3: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('Code3', Code3);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_BHPH_LOAN_REQUESTS}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress:
        options?.onUploadProgress &&
        (e => {
          const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
          options.onUploadProgress?.(Math.min(percent, 100));
        }),
    });
  }

  async getBHPHLoanRequestImportStatus(
    compId: string,
    logId: number | string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(
      `${routes.BHPH_LOAN_REQUESTS}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importFloorplanRequests(
    DMS: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_FLOORPLAN_REQUESTS}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            options.onUploadProgress?.(Math.min(percent, 100));
          }
        : undefined,
    });
  }

  async getFloorplanRequestImportStatus(
    compId: string,
    logId: number | string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(
      `${routes.FLOORPLAN_REQUESTS}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importIndirectLoanRquests(
    DMS: string,
    Code3: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('Code3', Code3);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_INDIRECT_LOADN_REQUESTS}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress:
        options?.onUploadProgress &&
        (e => {
          const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
          options.onUploadProgress?.(Math.min(percent, 100));
        }),
    });
  }

  async getIndirectLoanRequestImportStatus(
    compId: string,
    logId: number | string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(
      `${routes.INDIRECT_LOAN_REQUESTS}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importInterestBalance(
    DMS: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_INTEREST_BALANCE}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            options.onUploadProgress?.(Math.min(percent, 100));
          }
        : undefined,
    });
  }

  async getInterestBalanceImportStatus(compId: string, logId: number | string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${routes.INTEREST_BALANCE}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importLoanRequest(
    DMS: string,
    Code3: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('Code3', Code3);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_LOAN_REQUEST}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            options.onUploadProgress?.(Math.min(percent, 100));
          }
        : undefined,
    });
  }

  async getLoanRequestImportStatus(
    compId: string,
    logId: number | string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(
      `${routes.LOAN_REQUESTS}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importPremierLoanRequest(
    DMS: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_PREMIER_LOAN_REQUEST}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            options.onUploadProgress?.(Math.min(percent, 100));
          }
        : undefined,
    });
  }

  async getPremierLoanRequestImportStatus(
    compId: string,
    logId: number | string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(
      `${routes.PREMIER_LOAN_REQUESTS}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importRefSwapRequests(
    DMS: string,
    Code3: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('Code3', Code3);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_REF_SWAP_REQUESTS}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress:
        options?.onUploadProgress &&
        (e => {
          const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
          options.onUploadProgress?.(Math.min(percent, 100));
        }),
    });
  }

  async getRefiSwapImportStatus(
    compId: string,
    logId: number | string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(
      `${routes.REF_SWAP_REQUESTS}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importUnsoldInventory(
    DMS: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_UNSOLD_INVENTORY}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            options.onUploadProgress?.(Math.min(percent, 100));
          }
        : undefined,
    });
  }

  async getUnsoldInventoryImportStatus(compId: string, logId: number | string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${routes.UNSOLD_INVENTORY}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importVinStockNumXref(
    DMS: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_VIN_TO_STOCK_NUM_XREF}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            options.onUploadProgress?.(Math.min(percent, 100));
          }
        : undefined,
    });
  }

  async getVinToStockNumXrefImportStatus(
    compId: string,
    logId: number | string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(
      `${routes.VIN_TO_STOCK_NUM_XREF}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async importWholesaleInventory(
    DMS: string,
    compId: string,
    importDate: string,
    byUserName: string,
    file: File,
    options?: { onUploadProgress?: (percent: number) => void }
  ) {
    const formData = new FormData();
    formData.append('DMS', DMS);
    formData.append('compId', compId.toString());
    formData.append('importDate', importDate);
    formData.append('byUserName', byUserName);
    formData.append('file', file);

    return authenticatedAxiosInstance.post(`${routes.IMPORT_WHOLESALE_INVENTORY}`, formData, {
      headers: {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress
        ? (e) => {
            const percent = e.total ? Math.round((e.loaded * 100) / e.total) : 0;
            options.onUploadProgress?.(Math.min(percent, 100));
          }
        : undefined,
    });
  }

  async getWholesaleInventoryImportStatus(compId: string, logId: number | string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${routes.WHOLESALE_INVENTORY}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async getUploadMonitorARAged(compId?: string, importDate?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_AR_AGED}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getArAgedImportStatus(compId: string, logId: number | string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      `${routes.AR_AGED_IMPORT_STATUS}/${compId}/import-status/${logId}`,
      { headers: authHeader(), signal }
    );
  }

  async getUploadMonitorARBalance(compId?: string, importDate?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_AR_BALANCE}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorAccDepreciation(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_ACC_DEPRECIATION}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorUnsoldInventory(compId?: string, importDate?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_UNSOLD_INVENTORY}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorWholesaleInventory(compId?: string, importDate?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_WHolesale_INVENTORY}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorInterestBalance(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_INTEREST_BALANCE}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorVinToStockNumXref(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_VIN_TO_STOCK_NUM_XREF}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorFloorplanRequest(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_FLOORPLAN_REQUEST}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorLoanRequest(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_LOAN_REQUEST}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorLoanRequestDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_LOAN_REQUEST_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorPremierLoanRequest(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_PREMIER_LOAN_REQUEST}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorIndirectLoanRequest(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_INDIRECT_LOAN_REQUEST}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorBhphLoanRequest(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_BHPH_LOAN_REQUEST}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorARAgedDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_AR_AGED_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorARBalanceDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_AR_BALANCE_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorAccDepreciationDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_ACC_DEPRECIATION_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorBhphLoanRequestDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_BHPH_LOAN_REQUEST_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorFloorplanRequestDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_FLOORPLAN_REQUEST_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorIndirectLoanRequestDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_INDIRECT_LOAN_REQUEST_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorInterestBalanceDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_INTEREST_BALANCE_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorPremierLoanRequestDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_PREMIER_LOAN_REQUEST_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorRefiSwapDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_REFI_SWAP_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorUnsoldInventoryDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_UNSOLD_INVENTORY_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorVinToStockDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_VIN_TO_STOCK_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  async getUploadMonitorWholesaleInventoryDetail(
    compId?: string,
    importDate?: string,
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(`${routes.UPLOAD_MONITOR_WHOLESALE_INVENTORY_DETAIL}`, {
      headers: authHeader(),
      params: { compId, importDate },
      signal,
    });
  }

  /**
   * Get API endpoint documentation for debugging purposes
   * @returns {Promise} - API response with endpoint documentation
   */
  getApiDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/imports/docs`, {
      headers: authHeader(),
      signal,
    });
  }

  /**
   * Get SQL procedure documentation for debugging purposes
   * @returns {Promise} - API response with SQL procedure documentation
   */
  getSqlDocumentation(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(`${process.env.REACT_APP_API_URL}/imports/sql-docs`, {
      headers: authHeader(),
      signal,
    });
  }
}

export default new ImportsService();
